import React from 'react';
import { Navigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ROUTES, USER_ROLES } from '../../constants';

// Loading spinner component
const LoadingSpinner = () => (
  <div className='min-h-screen flex items-center justify-center bg-gray-50'>
    <div className='flex flex-col items-center'>
      <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600'></div>
      <p className='mt-4 text-gray-600 font-medium'>Loading...</p>
    </div>
  </div>
);

// Access Denied component
const AccessDenied = ({ reason, requiredRole, currentRole }) => (
  <div className='min-h-screen flex items-center justify-center bg-gray-50 px-4 sm:px-6 lg:px-8'>
    <div className='max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center border border-gray-100'>
      <div className='mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 text-red-600 mb-6'>
        <svg
          className='h-8 w-8'
          fill='none'
          viewBox='0 0 24 24'
          stroke='currentColor'
        >
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M12 15v2m0 0v2m0-2h2m-2 0H10m4-6a4 4 0 10-8 0v2h8V9z'
          />
        </svg>
      </div>

      <span className='inline-block px-3 py-1 text-xs font-semibold text-red-700 bg-red-100 rounded-full mb-3 uppercase tracking-wider'>
        403 Forbidden
      </span>

      <h1 className='text-2xl font-bold text-gray-900 mb-2'>
        Access Denied
      </h1>

      <p className='text-gray-600 mb-6'>
        {reason || "You don't have the administrative permissions required to access this resource."}
      </p>

      {(requiredRole || currentRole) && (
        <div className='bg-gray-50 rounded-lg p-4 mb-6 text-xs text-left space-y-1.5 border border-gray-200'>
          {requiredRole && (
            <div className='flex justify-between'>
              <span className='text-gray-500'>Required Access:</span>
              <span className='font-semibold text-gray-800 capitalize'>{requiredRole}</span>
            </div>
          )}
          <div className='flex justify-between'>
            <span className='text-gray-500'>Your Current Role:</span>
            <span className='font-semibold text-gray-800 capitalize'>{currentRole || 'Unknown'}</span>
          </div>
        </div>
      )}

      <Link
        to={ROUTES.DASHBOARD}
        className='inline-flex items-center justify-center w-full px-5 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors duration-150 shadow-sm'
      >
        <svg
          className='w-4 h-4 mr-2'
          fill='none'
          stroke='currentColor'
          viewBox='0 0 24 24'
        >
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M10 19l-7-7m0 0l7-7m-7 7h18'
          />
        </svg>
        Back to Dashboard
      </Link>
    </div>
  </div>
);

const ProtectedRoute = ({
  children,
  requiredRole = null,
  minRole = null,
  adminOnly = false,
  requiredPermission = null,
}) => {
  const location = useLocation();
  const {
    isAuthenticated,
    isLoading,
    hasRole,
    hasPermission,
    isAdminOrAbove,
    hasRoleOrAbove,
    user,
  } = useAuth();

  // Show loading spinner while auth is initializing
  if (isLoading) {
    return <LoadingSpinner />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to={ROUTES.LOGIN} state={{ from: location }} replace />;
  }

  // Check adminOnly requirement
  if (adminOnly && !isAdminOrAbove()) {
    return (
      <AccessDenied
        reason='This section requires Administrator or Super Administrator privileges.'
        requiredRole='Administrator or above'
        currentRole={user?.role}
      />
    );
  }

  // Check minRole requirement
  if (minRole && !hasRoleOrAbove(minRole)) {
    const minRoleLabel =
      minRole === USER_ROLES.ADMIN
        ? 'Administrator or above'
        : minRole === USER_ROLES.SUPER_ADMIN
        ? 'Super Administrator'
        : minRole;
    return (
      <AccessDenied
        reason={`This section requires at least ${minRoleLabel} privileges.`}
        requiredRole={minRoleLabel}
        currentRole={user?.role}
      />
    );
  }

  // Check specific role requirement if specified
  if (requiredRole && !hasRole(requiredRole)) {
    return (
      <AccessDenied
        reason="You don't have the specific role required for this page."
        requiredRole={requiredRole}
        currentRole={user?.role}
      />
    );
  }

  // Check permission requirement if specified
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return (
      <AccessDenied
        reason={`Missing required permission: ${requiredPermission}`}
        currentRole={user?.role}
      />
    );
  }

  return children;
};

export default ProtectedRoute;
